package diana.soleil.design.iraniancanadienbazzar.core.data.firestore

import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.GemsRepository
import diana.soleil.design.iraniancanadienbazzar.core.firebase.AuthService
import diana.soleil.design.iraniancanadienbazzar.core.firebase.FirebaseServices
import diana.soleil.design.iraniancanadienbazzar.core.model.Fact
import diana.soleil.design.iraniancanadienbazzar.core.model.FactCategory
import diana.soleil.design.iraniancanadienbazzar.core.model.GemType
import diana.soleil.design.iraniancanadienbazzar.core.model.Joke
import diana.soleil.design.iraniancanadienbazzar.core.model.JokeCategory
import diana.soleil.design.iraniancanadienbazzar.core.model.JokeMaturity
import diana.soleil.design.iraniancanadienbazzar.core.model.Recipe
import diana.soleil.design.iraniancanadienbazzar.core.model.RecipeCategory
import diana.soleil.design.iraniancanadienbazzar.core.model.RecipeDifficulty
import diana.soleil.design.iraniancanadienbazzar.core.model.RecipeIngredient
import diana.soleil.design.iraniancanadienbazzar.core.model.RecipeRegion
import diana.soleil.design.iraniancanadienbazzar.core.model.RecipeStep
import diana.soleil.design.iraniancanadienbazzar.core.model.SavedGem
import diana.soleil.design.iraniancanadienbazzar.core.model.Poem
import diana.soleil.design.iraniancanadienbazzar.core.model.PoemForm
import diana.soleil.design.iraniancanadienbazzar.core.model.Poet
import diana.soleil.design.iraniancanadienbazzar.core.model.Story
import diana.soleil.design.iraniancanadienbazzar.core.model.StoryRegion
import diana.soleil.design.iraniancanadienbazzar.core.model.Tale
import dev.gitlive.firebase.firestore.BaseTimestamp
import dev.gitlive.firebase.firestore.Direction
import dev.gitlive.firebase.firestore.FirebaseFirestore
import dev.gitlive.firebase.firestore.FirebaseFirestoreException
import dev.gitlive.firebase.firestore.FirestoreExceptionCode
import dev.gitlive.firebase.firestore.Timestamp
import dev.gitlive.firebase.firestore.code
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.TimeoutCancellationException
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.serialization.Serializable

private const val JOKES = "jokes"
private const val RECIPES = "recipes"
private const val FACTS = "facts"
private const val TALES = "tales"
private const val POETS = "poets"
private const val POEMS = "poems"
private const val STORIES = "stories"
// LIKES / USERS come from FirestoreCommon.kt (same values, shared across repositories).
private const val SAVED_GEMS = "savedGems"
private const val STATUS_VISIBLE = "visible"
private const val MATURITY_CLEAN = "clean"

/**
 * Live [GemsRepository] over Cloud Firestore (GitLive) — `jokes` + `recipes` collections plus the
 * posts-style per-doc `likes/{uid}` subcollections and `users/{uid}/savedGems` bookmarks (see
 * `docs/DATA_MODEL.md` § Hidden Gems). Queries mirror the news/posts conventions: server-side
 * visibility filters + `publishedAt desc` ordering (composite indexes in firestore.indexes.json);
 * documents that fail to decode are skipped rather than failing the stream.
 */
@OptIn(ExperimentalCoroutinesApi::class)
class FirestoreGemsRepository(
    firebase: FirebaseServices,
    private val auth: AuthService,
) : GemsRepository {

    private val firestore: FirebaseFirestore = firebase.firestore

    override val currentUid: String? get() = auth.currentUser?.takeIf { !it.isAnonymous }?.uid

    override fun observeJokes(includeAdult: Boolean, limit: Int): Flow<DataResult<List<Joke>>> {
        var query = firestore.collection(JOKES)
            .where { "visible" equalTo true }
            .where { "status" equalTo STATUS_VISIBLE }
        // The clean filter is part of the RULES proof for signed-out sessions (adult docs deny
        // anonymous reads, so an unfiltered list query is rejected wholesale) — callers only pass
        // includeAdult=true for a signed-in, opted-in user with the RC tier enabled.
        if (!includeAdult) query = query.where { "maturity" equalTo MATURITY_CLEAN }
        return query.orderBy("publishedAt", Direction.DESCENDING).limit(limit).snapshots
            .map { snap ->
                val jokes = snap.documents.mapNotNull { doc ->
                    runCatching { doc.data<JokeDoc>().toModel(doc.id) }.getOrNull()
                }
                DataResult.Success(jokes) as DataResult<List<Joke>>
            }
            .catch { emit(DataResult.Failure(it.toGemsError())) }
    }

    override fun observeRecipes(limit: Int): Flow<DataResult<List<Recipe>>> =
        firestore.collection(RECIPES)
            .where { "visible" equalTo true }
            .where { "status" equalTo STATUS_VISIBLE }
            .orderBy("publishedAt", Direction.DESCENDING).limit(limit).snapshots
            .map { snap ->
                val recipes = snap.documents.mapNotNull { doc ->
                    runCatching { doc.data<RecipeDoc>().toModel(doc.id) }.getOrNull()
                }
                DataResult.Success(recipes) as DataResult<List<Recipe>>
            }
            .catch { emit(DataResult.Failure(it.toGemsError())) }

    override fun observeFacts(limit: Int): Flow<DataResult<List<Fact>>> =
        firestore.collection(FACTS)
            .where { "visible" equalTo true }
            .where { "status" equalTo STATUS_VISIBLE }
            .orderBy("publishedAt", Direction.DESCENDING).limit(limit).snapshots
            .map { snap ->
                val facts = snap.documents.mapNotNull { doc ->
                    runCatching { doc.data<FactDoc>().toModel(doc.id) }.getOrNull()
                }
                DataResult.Success(facts) as DataResult<List<Fact>>
            }
            .catch { emit(DataResult.Failure(it.toGemsError())) }

    override fun observeTales(limit: Int): Flow<DataResult<List<Tale>>> =
        firestore.collection(TALES)
            .where { "visible" equalTo true }
            .where { "status" equalTo STATUS_VISIBLE }
            .orderBy("publishedAt", Direction.DESCENDING).limit(limit).snapshots
            .map { snap ->
                val tales = snap.documents.mapNotNull { doc ->
                    runCatching { doc.data<TaleDoc>().toModel(doc.id) }.getOrNull()
                }
                DataResult.Success(tales) as DataResult<List<Tale>>
            }
            .catch { emit(DataResult.Failure(it.toGemsError())) }

    override suspend fun getTale(taleId: String): DataResult<Tale> = try {
        val snapshot = firestore.collection(TALES).document(taleId).get()
        val tale = if (snapshot.exists) snapshot.data<TaleDoc>().toModel(snapshot.id) else null
        if (tale == null) DataResult.Failure(AppError.NotFound()) else DataResult.Success(tale)
    } catch (t: Throwable) {
        DataResult.Failure(t.toGemsError())
    }

    // ── Persian poetry ──────────────────────────────────────────────────────
    // Poets are ordered by `orderIndex` (chronological) rather than publishedAt: the hub reads as a
    // shelf of names, not a feed.
    override fun observePoets(limit: Int): Flow<DataResult<List<Poet>>> =
        firestore.collection(POETS)
            .where { "visible" equalTo true }
            .where { "status" equalTo STATUS_VISIBLE }
            .orderBy("orderIndex").limit(limit).snapshots
            .map { snap ->
                val poets = snap.documents.mapNotNull { doc ->
                    runCatching { doc.data<PoetDoc>().toModel(doc.id) }.getOrNull()
                }
                DataResult.Success(poets) as DataResult<List<Poet>>
            }
            .catch { emit(DataResult.Failure(it.toGemsError())) }

    override fun observePoems(poetId: String?, limit: Int): Flow<DataResult<List<Poem>>> =
        firestore.collection(POEMS)
            .let { all -> if (poetId == null) all.where { "visible" equalTo true } else all.where { "poetId" equalTo poetId }.where { "visible" equalTo true } }
            .where { "status" equalTo STATUS_VISIBLE }
            .orderBy("publishedAt", Direction.DESCENDING).limit(limit).snapshots
            .map { snap ->
                val poems = snap.documents.mapNotNull { doc ->
                    runCatching { doc.data<PoemDoc>().toModel(doc.id) }.getOrNull()
                }
                DataResult.Success(poems) as DataResult<List<Poem>>
            }
            .catch { emit(DataResult.Failure(it.toGemsError())) }

    override suspend fun getPoem(poemId: String): DataResult<Poem> = try {
        val snapshot = firestore.collection(POEMS).document(poemId).get()
        val poem = if (snapshot.exists) snapshot.data<PoemDoc>().toModel(snapshot.id) else null
        if (poem == null) DataResult.Failure(AppError.NotFound()) else DataResult.Success(poem)
    } catch (t: Throwable) {
        DataResult.Failure(t.toGemsError())
    }

    // ── Short stories ───────────────────────────────────────────────────────
    override fun observeStories(limit: Int): Flow<DataResult<List<Story>>> =
        firestore.collection(STORIES)
            .where { "visible" equalTo true }
            .where { "status" equalTo STATUS_VISIBLE }
            .orderBy("publishedAt", Direction.DESCENDING).limit(limit).snapshots
            .map { snap ->
                val stories = snap.documents.mapNotNull { doc ->
                    runCatching { doc.data<StoryDoc>().toModel(doc.id) }.getOrNull()
                }
                DataResult.Success(stories) as DataResult<List<Story>>
            }
            .catch { emit(DataResult.Failure(it.toGemsError())) }

    override suspend fun getStory(storyId: String): DataResult<Story> = try {
        val snapshot = firestore.collection(STORIES).document(storyId).get()
        val story = if (snapshot.exists) snapshot.data<StoryDoc>().toModel(snapshot.id) else null
        if (story == null) DataResult.Failure(AppError.NotFound()) else DataResult.Success(story)
    } catch (t: Throwable) {
        DataResult.Failure(t.toGemsError())
    }

    override suspend fun getRecipe(recipeId: String): DataResult<Recipe> = try {
        val snapshot = firestore.collection(RECIPES).document(recipeId).get()
        val recipe = if (snapshot.exists) snapshot.data<RecipeDoc>().toModel(snapshot.id) else null
        if (recipe == null) DataResult.Failure(AppError.NotFound()) else DataResult.Success(recipe)
    } catch (t: Throwable) {
        DataResult.Failure(t.toGemsError())
    }

    // Collection-group stream over every `likes/{uid}` doc the caller wrote (posts pattern — one
    // listener answers "did I like it" for the whole corpus). Post like docs carry `postId` and
    // decode to null gem ids here, so they simply drop out of the gem sets (and vice versa).
    override fun observeLikedJokeIds(): Flow<Set<String>> = observeMyLikes { it.jokeId }

    override fun observeLikedRecipeIds(): Flow<Set<String>> = observeMyLikes { it.recipeId }

    override fun observeLikedFactIds(): Flow<Set<String>> = observeMyLikes { it.factId }

    override fun observeLikedTaleIds(): Flow<Set<String>> = observeMyLikes { it.taleId }

    override fun observeLikedPoemIds(): Flow<Set<String>> = observeMyLikes { it.poemId }

    override fun observeLikedStoryIds(): Flow<Set<String>> = observeMyLikes { it.storyId }

    override suspend fun setPoemLiked(poemId: String, liked: Boolean): DataResult<Unit> =
        setLiked(POEMS, poemId, liked) { uid ->
            PoemLikeCreateDoc(uid = uid, poemId = poemId, createdAt = Timestamp.ServerTimestamp)
        }

    override suspend fun setStoryLiked(storyId: String, liked: Boolean): DataResult<Unit> =
        setLiked(STORIES, storyId, liked) { uid ->
            StoryLikeCreateDoc(uid = uid, storyId = storyId, createdAt = Timestamp.ServerTimestamp)
        }

    private fun observeMyLikes(idOf: (GemLikeReadDoc) -> String?): Flow<Set<String>> =
        auth.authState.flatMapLatest { user ->
            val me = user?.takeIf { !it.isAnonymous }?.uid
            if (me == null) {
                flowOf(emptySet())
            } else {
                firestore.collectionGroup(LIKES).where { "uid" equalTo me }.snapshots
                    .map { snap ->
                        snap.documents
                            .mapNotNull { doc -> runCatching { idOf(doc.data<GemLikeReadDoc>()) }.getOrNull() }
                            .filterNotNull()
                            .toSet()
                    }
                    .catch { emit(emptySet()) }
            }
        }

    override suspend fun setJokeLiked(jokeId: String, liked: Boolean): DataResult<Unit> =
        setLiked(JOKES, jokeId, liked) { uid ->
            JokeLikeCreateDoc(uid = uid, jokeId = jokeId, createdAt = Timestamp.ServerTimestamp)
        }

    override suspend fun setRecipeLiked(recipeId: String, liked: Boolean): DataResult<Unit> =
        setLiked(RECIPES, recipeId, liked) { uid ->
            RecipeLikeCreateDoc(uid = uid, recipeId = recipeId, createdAt = Timestamp.ServerTimestamp)
        }

    override suspend fun setFactLiked(factId: String, liked: Boolean): DataResult<Unit> =
        setLiked(FACTS, factId, liked) { uid ->
            FactLikeCreateDoc(uid = uid, factId = factId, createdAt = Timestamp.ServerTimestamp)
        }

    override suspend fun setTaleLiked(taleId: String, liked: Boolean): DataResult<Unit> =
        setLiked(TALES, taleId, liked) { uid ->
            TaleLikeCreateDoc(uid = uid, taleId = taleId, createdAt = Timestamp.ServerTimestamp)
        }

    private suspend inline fun <reified T : Any> setLiked(
        collection: String,
        gemId: String,
        liked: Boolean,
        doc: (uid: String) -> T,
    ): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            val ref = firestore.collection(collection).document(gemId).collection(LIKES).document(user.uid)
            if (liked) ref.set(doc(user.uid)) else ref.delete()
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toGemsError())
        }
    }

    override fun observeSavedGemsCloud(): Flow<Set<SavedGem>?> =
        auth.authState.flatMapLatest { user ->
            val me = user?.takeIf { !it.isAnonymous }?.uid
            if (me == null) {
                flowOf(null)
            } else {
                firestore.collection(USERS).document(me).collection(SAVED_GEMS).snapshots
                    .map { snap ->
                        val gems: Set<SavedGem>? = snap.documents.mapNotNull { doc ->
                            val type = runCatching { doc.data<SavedGemReadDoc>().type }.getOrNull()
                            GemType.fromWire(type)?.let { SavedGem(id = doc.id, type = it) }
                        }.toSet()
                        gems
                    }
                    .catch { emit(null) }
            }
        }

    override suspend fun setSavedGemCloud(gem: SavedGem, saved: Boolean): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            val ref = firestore.collection(USERS).document(user.uid).collection(SAVED_GEMS).document(gem.id)
            if (saved) {
                ref.set(SavedGemCreateDoc(type = gem.type.wire, savedAt = Timestamp.ServerTimestamp))
            } else {
                ref.delete()
            }
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toGemsError())
        }
    }

    override fun observeAdultOptIn(): Flow<Boolean?> =
        auth.authState.flatMapLatest { user ->
            val me = user?.takeIf { !it.isAnonymous }?.uid
            if (me == null) {
                flowOf(null)
            } else {
                firestore.collection(USERS).document(me).snapshots
                    .map { snap ->
                        val optIn: Boolean? = if (snap.exists) {
                            runCatching { snap.data<AdultOptInReadDoc>().adultContentOptIn }.getOrNull()
                        } else {
                            null
                        }
                        optIn
                    }
                    .catch { emit(null) }
            }
        }

    override suspend fun setAdultOptIn(optedIn: Boolean): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            firestore.collection(USERS).document(user.uid)
                .set(AdultOptInWriteDoc(adultContentOptIn = optedIn), merge = true)
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toGemsError())
        }
    }
}

/** Same AppError mapping as the main repositories file (kept private there). */
private fun Throwable.toGemsError(): AppError = when (this) {
    is FirebaseFirestoreException -> when (code) {
        FirestoreExceptionCode.UNAVAILABLE,
        FirestoreExceptionCode.DEADLINE_EXCEEDED,
        -> AppError.Network(this)
        FirestoreExceptionCode.PERMISSION_DENIED,
        FirestoreExceptionCode.UNAUTHENTICATED,
        -> AppError.Unauthorized(this)
        FirestoreExceptionCode.NOT_FOUND -> AppError.NotFound(this)
        FirestoreExceptionCode.ALREADY_EXISTS -> AppError.Conflict(this)
        else -> AppError.Unknown(this)
    }
    is TimeoutCancellationException -> AppError.Network(this)
    else -> AppError.Unknown(this)
}

// ── Wire DTOs (GitLive decoder; tolerant defaults so partial docs never fail the stream) ──────────

@Serializable
internal data class JokeDoc(
    val text: LocalizedTextDoc = LocalizedTextDoc(),
    val category: String = "",
    val maturity: String = MATURITY_CLEAN,
    val tags: List<String> = emptyList(),
    val likeCount: Int = 0,
    val publishedAt: Timestamp? = null,
) {
    fun toModel(id: String): Joke? {
        val published = publishedAt ?: return null
        return Joke(
            id = id,
            text = text.toModel(),
            category = when (category) {
                "classic" -> JokeCategory.Classic
                "wordplay" -> JokeCategory.Wordplay
                "family" -> JokeCategory.Family
                "nostalgia" -> JokeCategory.Nostalgia
                else -> JokeCategory.Observational
            },
            maturity = if (maturity == "adult") JokeMaturity.Adult else JokeMaturity.Clean,
            tags = tags,
            likeCount = likeCount,
            publishedAtMillis = published.seconds * 1_000 + published.nanoseconds / 1_000_000,
        )
    }
}

@Serializable
internal data class RecipeIngredientDoc(
    val name: LocalizedTextDoc = LocalizedTextDoc(),
    val quantity: LocalizedTextDoc = LocalizedTextDoc(),
) {
    fun toModel(): RecipeIngredient = RecipeIngredient(name = name.toModel(), quantity = quantity.toModel())
}

@Serializable
internal data class RecipeStepDoc(
    val text: LocalizedTextDoc = LocalizedTextDoc(),
    val imageUrl: String? = null,
) {
    fun toModel(): RecipeStep = RecipeStep(text = text.toModel(), imageUrl = imageUrl)
}

@Serializable
internal data class RecipeDoc(
    val title: LocalizedTextDoc = LocalizedTextDoc(),
    val summary: LocalizedTextDoc = LocalizedTextDoc(),
    val story: LocalizedTextDoc? = null,
    val region: String = "",
    val category: String = "",
    val imageUrl: String? = null,
    val prepMinutes: Int = 0,
    val cookMinutes: Int = 0,
    val servings: Int = 0,
    val difficulty: String = "",
    val groceryQuery: String? = null,
    val ingredients: List<RecipeIngredientDoc> = emptyList(),
    val steps: List<RecipeStepDoc> = emptyList(),
    val tags: List<String> = emptyList(),
    val likeCount: Int = 0,
    val publishedAt: Timestamp? = null,
) {
    fun toModel(id: String): Recipe? {
        val published = publishedAt ?: return null
        return Recipe(
            id = id,
            title = title.toModel(),
            summary = summary.toModel(),
            story = story?.toModel(),
            region = RecipeRegion.fromWire(region),
            category = RecipeCategory.fromWire(category),
            imageUrl = imageUrl?.takeIf { it.isNotBlank() },
            prepMinutes = prepMinutes,
            cookMinutes = cookMinutes,
            servings = servings,
            difficulty = RecipeDifficulty.fromWire(difficulty),
            groceryQuery = groceryQuery?.takeIf { it.isNotBlank() },
            ingredients = ingredients.map { it.toModel() },
            steps = steps.map { it.toModel() },
            tags = tags,
            likeCount = likeCount,
            publishedAtMillis = published.seconds * 1_000 + published.nanoseconds / 1_000_000,
        )
    }
}

/**
 * Tolerant read shape for ANY doc in the `likes` collection group — post likes carry `postId`,
 * gem likes carry `jokeId`/`recipeId`; the absent ids stay null and the mapper picks its own.
 */
@Serializable
internal data class GemLikeReadDoc(
    val uid: String = "",
    val jokeId: String? = null,
    val recipeId: String? = null,
    val factId: String? = null,
    val taleId: String? = null,
    val poemId: String? = null,
    val storyId: String? = null,
)

/** Create shape for `jokes/{id}/likes/{uid}` — rules pin `hasOnly(['uid','jokeId','createdAt'])`. */
@Serializable
internal data class JokeLikeCreateDoc(
    val uid: String,
    val jokeId: String,
    val createdAt: BaseTimestamp,
)

/** Create shape for `recipes/{id}/likes/{uid}` — rules pin `hasOnly(['uid','recipeId','createdAt'])`. */
@Serializable
internal data class RecipeLikeCreateDoc(
    val uid: String,
    val recipeId: String,
    val createdAt: BaseTimestamp,
)

/** Create shape for `facts/{id}/likes/{uid}` — rules pin `hasOnly(['uid','factId','createdAt'])`. */
@Serializable
internal data class FactLikeCreateDoc(
    val uid: String,
    val factId: String,
    val createdAt: BaseTimestamp,
)

@Serializable
internal data class FactDoc(
    val text: LocalizedTextDoc = LocalizedTextDoc(),
    val category: String = "",
    val source: LocalizedTextDoc? = null,
    val tags: List<String> = emptyList(),
    val likeCount: Int = 0,
    val publishedAt: Timestamp? = null,
) {
    fun toModel(id: String): Fact? {
        val published = publishedAt ?: return null
        return Fact(
            id = id,
            text = text.toModel(),
            category = FactCategory.fromWire(category),
            source = source?.toModel(),
            tags = tags,
            likeCount = likeCount,
            publishedAtMillis = published.seconds * 1_000 + published.nanoseconds / 1_000_000,
        )
    }
}

/** Create shape for `tales/{id}/likes/{uid}` — rules pin `hasOnly(['uid','taleId','createdAt'])`. */
@Serializable
internal data class TaleLikeCreateDoc(
    val uid: String,
    val taleId: String,
    val createdAt: BaseTimestamp,
)

/** Create shape for `poems/{id}/likes/{uid}` — rules pin `hasOnly(['uid','poemId','createdAt'])`. */
@Serializable
internal data class PoemLikeCreateDoc(
    val uid: String,
    val poemId: String,
    val createdAt: BaseTimestamp,
)

/** Create shape for `stories/{id}/likes/{uid}` — rules pin `hasOnly(['uid','storyId','createdAt'])`. */
@Serializable
internal data class StoryLikeCreateDoc(
    val uid: String,
    val storyId: String,
    val createdAt: BaseTimestamp,
)

@Serializable
internal data class TaleDoc(
    val title: LocalizedTextDoc = LocalizedTextDoc(),
    val summary: LocalizedTextDoc = LocalizedTextDoc(),
    val body: LocalizedTextDoc = LocalizedTextDoc(),
    val moral: LocalizedTextDoc? = null,
    val imageUrl: String? = null,
    val characters: List<String> = emptyList(),
    val likeCount: Int = 0,
    val publishedAt: Timestamp? = null,
) {
    fun toModel(id: String): Tale? {
        val published = publishedAt ?: return null
        return Tale(
            id = id,
            title = title.toModel(),
            summary = summary.toModel(),
            body = body.toModel(),
            moral = moral?.toModel(),
            imageUrl = imageUrl?.takeIf { it.isNotBlank() },
            characters = characters,
            likeCount = likeCount,
            publishedAtMillis = published.seconds * 1_000 + published.nanoseconds / 1_000_000,
        )
    }
}

/** Read shape for `poets/{id}` — the shelf of poets in the poetry section. */
@Serializable
internal data class PoetDoc(
    val name: LocalizedTextDoc = LocalizedTextDoc(),
    val era: LocalizedTextDoc = LocalizedTextDoc(),
    val bio: LocalizedTextDoc = LocalizedTextDoc(),
    val imageUrl: String? = null,
    val orderIndex: Int = 0,
    val publishedAt: Timestamp? = null,
) {
    fun toModel(id: String): Poet? {
        val published = publishedAt ?: return null
        return Poet(
            id = id,
            name = name.toModel(),
            era = era.toModel(),
            bio = bio.toModel(),
            imageUrl = imageUrl?.takeIf { it.isNotBlank() },
            orderIndex = orderIndex,
            publishedAtMillis = published.seconds * 1_000 + published.nanoseconds / 1_000_000,
        )
    }
}

/** Read shape for `poems/{id}` — one poem, keyed to its poet by `poetId`. */
@Serializable
internal data class PoemDoc(
    val poetId: String = "",
    val title: LocalizedTextDoc = LocalizedTextDoc(),
    val body: LocalizedTextDoc = LocalizedTextDoc(),
    val note: LocalizedTextDoc? = null,
    val fal: LocalizedTextDoc? = null,
    val form: String? = null,
    val likeCount: Int = 0,
    val publishedAt: Timestamp? = null,
) {
    fun toModel(id: String): Poem? {
        val published = publishedAt ?: return null
        return Poem(
            id = id,
            poetId = poetId,
            title = title.toModel(),
            body = body.toModel(),
            note = note?.toModel(),
            fal = fal?.toModel(),
            form = PoemForm.fromWire(form),
            likeCount = likeCount,
            publishedAtMillis = published.seconds * 1_000 + published.nanoseconds / 1_000_000,
        )
    }
}

/** Read shape for `stories/{id}` — a regional folk story, retold by us. */
@Serializable
internal data class StoryDoc(
    val title: LocalizedTextDoc = LocalizedTextDoc(),
    val summary: LocalizedTextDoc = LocalizedTextDoc(),
    val body: LocalizedTextDoc = LocalizedTextDoc(),
    val region: String? = null,
    val moral: LocalizedTextDoc? = null,
    val imageUrl: String? = null,
    val likeCount: Int = 0,
    val publishedAt: Timestamp? = null,
) {
    fun toModel(id: String): Story? {
        val published = publishedAt ?: return null
        return Story(
            id = id,
            title = title.toModel(),
            summary = summary.toModel(),
            body = body.toModel(),
            region = StoryRegion.fromWire(region),
            moral = moral?.toModel(),
            imageUrl = imageUrl?.takeIf { it.isNotBlank() },
            likeCount = likeCount,
            publishedAtMillis = published.seconds * 1_000 + published.nanoseconds / 1_000_000,
        )
    }
}

/** Read shape for `users/{uid}/savedGems/{gemId}`. */
@Serializable
internal data class SavedGemReadDoc(
    val type: String = "",
)

/** Create shape for `users/{uid}/savedGems/{gemId}`. */
@Serializable
internal data class SavedGemCreateDoc(
    val type: String,
    val savedAt: BaseTimestamp,
)

/** Tolerant read shape for the 18+ opt-in flag on `users/{uid}` (other fields ignored). */
@Serializable
internal data class AdultOptInReadDoc(
    val adultContentOptIn: Boolean? = null,
)

/** Merge-write shape for the 18+ opt-in only (rules allowlist the key on self-writes). */
@Serializable
internal data class AdultOptInWriteDoc(
    val adultContentOptIn: Boolean,
)
